import { computed, inject, toValue } from 'vue'
import { slugify } from '@/utils/stringParsing.js'
import { getShortName, isMaternitySize } from '@/utils/styleColor.js'
import { storeToRefs } from 'pinia'
import { useClosetStore } from '@shared/stores/closet.js'
import { useCollectionsStore } from '@shared/stores/collections.js'
import { useStyleProfileStore } from '@/stores/styleProfile.js'

export default function useStyleColor (styleColor) {
  const closetStore = useClosetStore()
  const collectionsStore = useCollectionsStore()
  const { allSizeOrders, plusSizeOptions, sizeLetterMapping, waistSizeOptionsMapped } = storeToRefs(useStyleProfileStore())

  const useRealLooks = inject('useRealLooks', false)

  const id = computed(() => toValue(styleColor)?.id)
  const style = computed(() => toValue(styleColor)?.style)
  const brand = computed(() => style.value?.brand)
  const name = computed(() => style.value?.name)
  const frontImage = computed(() => toValue(styleColor)?.images?.front)
  const realLookImages = computed(() => toValue(styleColor)?.images?.realLook)
  const useRealLook = computed(() => toValue(useRealLooks) === true)
  const realLookImage = computed(() => useRealLook.value ? realLookImages.value?.[0] : null)
  const previewImage = computed(() => {
    const image = realLookImage.value ?? frontImage.value
    return image
      ? {
          ...image,
          url: image.url.replace('_d', '_m')
        }
      : null
  })
  const backImage = computed(() => toValue(styleColor)?.images?.back)
  const discontinued = computed(() => toValue(styleColor)?.discontinued ?? false)
  const itemTypes = computed(() => toValue(styleColor)?.itemTypes ?? [])
  const unavailable = computed(() => !itemTypes.value.some(itemType => [true, undefined].includes(itemType.isMemberSize) && itemType.numAvailable > 0))
  const stocking = computed(() => itemTypes.value.some(itemType => itemType.stocking))
  const retailPrice = computed(() => Math.round(style.value?.retailPrice ?? 0))
  const minPrice = computed(() => toValue(styleColor)?.priceRange?.minPrice ?? null)
  const maxPrice = computed(() => toValue(styleColor)?.priceRange?.maxPrice ?? null)
  const hasPriceRange = computed(() => !!minPrice.value && !!maxPrice.value && minPrice.value !== maxPrice.value)
  const latestReturnDate = computed(() => toValue(styleColor)?.latestReturnDate ? new Date(toValue(styleColor).latestReturnDate) : null)
  const firstAvailableDate = computed(() => toValue(styleColor)?.firstAvailable ? new Date(toValue(styleColor).firstAvailable) : null)
  const pairsWith = computed(() => toValue(styleColor)?.pairsWith ?? [])

  const rating = computed(() => closetStore.ratingsMap[toValue(styleColor)?.id])
  const isFavorited = computed(() => rating.value === 1)
  const isCollected = computed(() => isFavorited.value || collectionsStore.collectedStyleColors.includes(toValue(styleColor)?.id))
  const isDisliked = computed(() => rating.value === -1)

  const additionalColors = computed(() => toValue(styleColor)?.additionalColors ?? [])
  const allImages = computed(() => {
    const images = toValue(styleColor)?.images
    if (!images?.front) return []
    const res = [images.front]
    if (images.back) res.push(images.back)
    if (images.alternative) res.push(...images.alternative)
    return res
  })
  const bullets = computed(() => toValue(styleColor)?.bullets ?? [])
  const color = computed(() => {
    const c = toValue(styleColor)?.color
    return c === 'Multi' ? c + '-color' : c
  })
  const fitType = computed(() => {
    const ft = toValue(styleColor)?.style?.fitType
    return ft === 'Relaxed' ? ft + ' fit' : ft
  })
  const itemTypesSortedBySize = computed(() => {
    const sizeOrders = allSizeOrders.value?.[style.value?.department]
    const raw = toValue(styleColor)?.itemTypes ?? []
    const noDeptHandler = raw[0] ? parseInt(raw[0].trueSize) : NaN
    const itemTypes = [...raw]
    if (sizeOrders) {
      return itemTypes.sort((a, b) =>
        a.trueSize in sizeOrders && b.trueSize in sizeOrders
          ? sizeOrders[a.trueSize] - sizeOrders[b.trueSize]
          : 0)
    }
    if (typeof noDeptHandler === 'number' && !Number.isNaN(noDeptHandler)) {
      return itemTypes.sort((a, b) => parseInt(a.trueSize) - parseInt(b.trueSize))
    }
    return itemTypes
  })
  const recommendedSize = computed(() => toValue(styleColor)?.recommendedSize)
  const recommendedSizeLoaded = computed(() => 'recommendedSize' in (toValue(styleColor) ?? {}))
  const plusOptionsAllSizeFormats = computed(() => {
    if (!plusSizeOptions.value) return null
    const filteredValues = plusSizeOptions.value.map(option => sizeLetterMapping.value[option]).filter(Boolean)
    return [...filteredValues, ...plusSizeOptions.value]
  })
  const allItemTypesSortedBySizeFormatted = computed(() => {
    const rec = toValue(styleColor)?.recommendedSize
    return itemTypesSortedBySize.value.map(itemType => ({
      ...itemType,
      value: itemType.trueSize,
      text: itemType.trueSize,
      disabled: itemType.numAvailable === 0,
      recommended: itemType.trueSize === rec,
    }))
  })

  const standardItemTypes = computed(() => {
    const matchingValues = itemTypesSortedBySize.value.filter(item =>
      !plusOptionsAllSizeFormats.value?.includes(item.trueSize) &&
      !isMaternitySize(item.trueSize) &&
      !isWaistSize(item.trueSize)
    )
    return mapItemTypes(matchingValues)
  })
  const plusItemTypes = computed(() => mapItemTypes(
    itemTypesSortedBySize.value.filter(item =>
      plusOptionsAllSizeFormats.value?.includes(item.trueSize)
    )
  ))
  const maternityItemTypes = computed(() => mapItemTypes(
    itemTypesSortedBySize.value.filter(item => isMaternitySize(item.trueSize))
  ))
  const waistSizeInInchesItemTypes = computed(() => mapItemTypes(
    itemTypesSortedBySize.value.filter(item => isWaistSize(item.trueSize))
  ))
  const hasStandardItemTypes = computed(() => standardItemTypes.value.length > 0)
  const hasPlusItemTypes = computed(() => plusItemTypes.value.length > 0)
  const hasMaternityItemTypes = computed(() => maternityItemTypes.value.length > 0)
  const isNewlyAdded = computed(() => toValue(styleColor)?.firstAvailable === null)
  const isAvailable = computed(() => (toValue(styleColor)?.itemTypes?.length ?? 0) > 0)
  const isDiscontinued = computed(() => !isAvailable.value)
  const isUnreviewed = computed(() => {
    const sc = toValue(styleColor)
    return sc?.writtenReviews?.count === 0 &&
      (sc?.clientReviews?.length ?? 0) === 0 &&
      (sc?.expertReviews?.length ?? 0) === 0
  })
  const writtenReviewsCount = computed(() => toValue(styleColor)?.writtenReviews?.count ?? 0)
  const totalReviews = computed(() => toValue(styleColor)?.totalReviewsCount)
  const maxPercentOff = computed(() =>
    toValue(styleColor)?.priceRange ? Math.round(((retailPrice.value - minPrice.value) / retailPrice.value) * 100) : null
  )
  const minPercentOff = computed(() =>
    toValue(styleColor)?.priceRange ? Math.round(((retailPrice.value - maxPrice.value) / retailPrice.value) * 100) : null
  )
  const materials = computed(() => style.value?.materials ?? [])
  const shortName = computed(() => getShortName(toValue(styleColor)))
  const sizeGuideUrl = computed(() =>
    style.value?.sizeGuide ? `${import.meta.env.VITE_STYLE_GUIDE_URL_BASE}` + style.value.sizeGuide : null
  )
  const inseam = computed(() => {
    const list = []
    if (style.value?.inseam) list.push(style.value.inseam + ' inches')
    if (style.value?.inseam_2) list.push(style.value.inseam_2 + ' inches')
    if (style.value?.inseam_3) list.push(style.value.inseam_3 + ' inches')
    return list
  })
  const rise = computed(() => style.value?.rise)
  const brandPageLink = computed(() => ({
    name: 'brand',
    params: {
      name: slugify(style.value?.brand ?? ''),
      brandId: style.value?.brandId
    }
  }))
  const handmadeGarment = computed(() =>
    style.value?.handmadeGarment
      ? 'Due to the handmade nature of this garment, some variations in color may occur.'
      : null
  )
  const patternVariation = computed(() => toValue(styleColor)?.patternVariation ? 'Pattern may vary from photo.' : null)
  const beltRemoved = computed(() => toValue(styleColor)?.beltRemoved ? 'Belt not included.' : null)

  function mapItemTypes (itemTypes) {
    return itemTypes.map(item => ({
      ...item,
      value: item.trueSize,
      text: item.trueSize,
      disabled: item.numAvailable === 0,
      recommended: item.trueSize === toValue(styleColor)?.recommendedSize
    }))
  }

  function isWaistSize (size) {
    return waistSizeOptionsMapped.value.some(option => option.value === size)
  }

  function getPercentOff (priceClient) {
    return Math.round(((retailPrice.value - priceClient) / retailPrice.value) * 100)
  }

  async function recordClick (source, sourceIndex) {
    await closetStore.recordClick({ styleColorId: toValue(styleColor)?.id, source, sourceIndex })
  }

  return {
    id,
    style,
    brand,
    name,
    frontImage,
    realLookImages,
    realLookImage,
    useRealLook,
    previewImage,
    backImage,
    discontinued,
    itemTypes,
    unavailable,
    stocking,
    retailPrice,
    price: retailPrice,
    minPrice,
    maxPrice,
    hasPriceRange,
    latestReturnDate,
    firstAvailableDate,
    pairsWith,
    rating,
    isFavorited,
    isCollected,
    isDisliked,
    additionalColors,
    allImages,
    bullets,
    color,
    fitType,
    itemTypesSortedBySize,
    recommendedSize,
    recommendedSizeLoaded,
    plusOptionsAllSizeFormats,
    allItemTypesSortedBySizeFormatted,
    standardItemTypes,
    plusItemTypes,
    maternityItemTypes,
    waistSizeInInchesItemTypes,
    hasStandardItemTypes,
    hasPlusItemTypes,
    hasMaternityItemTypes,
    isNewlyAdded,
    isAvailable,
    isDiscontinued,
    isUnreviewed,
    writtenReviewsCount,
    totalReviews,
    maxPercentOff,
    minPercentOff,
    materials,
    shortName,
    sizeGuideUrl,
    inseam,
    rise,
    brandPageLink,
    handmadeGarment,
    patternVariation,
    beltRemoved,
    getPercentOff,
    mapItemTypes,
    isWaistSize,
    recordClick
  }
}

export function useStyleColorId (styleColorId) {
  const { styleColorsMap } = storeToRefs(useClosetStore())
  const styleColor = computed(() => styleColorsMap.value[toValue(styleColorId)])
  return useStyleColor(styleColor)
}
